import React, { cloneElement, isValidElement } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box } from "@xsolla/xui-primitives";
import { useResolvedTheme, type ThemeOverrideProps } from "@xsolla/xui-core";
import {
  IconButton,
  type ButtonProps,
  type IconButtonProps,
} from "@xsolla/xui-button";
import { Typography } from "@xsolla/xui-typography";
import { Remove } from "@xsolla/xui-icons-base";

export type ToastType = "alert" | "success" | "warning" | "info";

export type ToastActionElement = React.ReactElement<
  ButtonProps | IconButtonProps
>;

export interface ToastProps extends ThemeOverrideProps {
  /** Visual variant/tone of the toast */
  type?: ToastType;
  /** Title text (required for accessibility) */
  title?: string;
  /** Optional description text shown beneath the title */
  description?: string;
  /**
   * Action element (Button/IconButton). The toast will inject
   * `size="xs"`, `variant="tertiary"`, and a tone matching the toast type.
   */
  action?: React.ReactElement;
  /** Show/hide close button */
  showCloseButton?: boolean;
  /** Close button click handler */
  onClose?: () => void;
  testID?: string;
}

const TYPE_TO_BUTTON_TONE: Record<
  ToastType,
  "alert" | "brandExtra" | "brand" | "mono"
> = {
  alert: "alert",
  success: "brandExtra",
  warning: "mono",
  info: "brand",
};

export const Toast: React.FC<ToastProps> = ({
  type = "info",
  title,
  description,
  action,
  showCloseButton = true,
  onClose,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const config = theme.sizing.toast();

  const stripeColor: Record<ToastType, string> = {
    alert: theme.colors.content.alert.primary,
    success: theme.colors.content.success.secondary,
    warning: theme.colors.content.warning.secondary,
    info: theme.colors.content.brand.secondary,
  };

  const hasActions = Boolean(action) || showCloseButton;

  return (
    <Box
      backgroundColor={theme.colors.background.primary}
      borderRadius={config.borderRadius}
      flexDirection="row"
      alignItems="stretch"
      overflow="hidden"
      width={440}
      style={{ boxShadow: theme.shadow.surface }}
      testID={testID}
      role={type === "alert" ? "alert" : "status"}
      aria-live={type === "alert" ? "assertive" : "polite"}
      aria-label={`${type} toast`}
    >
      {/* Colored left stripe */}
      <Box backgroundColor={stripeColor[type]} width={4} alignSelf="stretch" />

      {/* Body */}
      <Box
        flex={1}
        flexDirection="row"
        alignItems="center"
        paddingHorizontal={16}
        paddingVertical={12}
        gap={16}
      >
        {/* Text */}
        <Box flex={1} gap={2}>
          {title && (
            <Typography
              variant="bodyMdAccent"
              color={theme.colors.content.primary}
            >
              {title}
            </Typography>
          )}
          {description && (
            <Typography variant="bodySm" color={theme.colors.content.tertiary}>
              {description}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        {hasActions && (
          <Box
            flexDirection="row"
            alignItems="center"
            alignSelf="stretch"
            gap={config.groupGap ?? 4}
          >
            {isValidElement(action) &&
              cloneElement(action as ToastActionElement, {
                size: "xs",
                variant: "tertiary",
                tone: TYPE_TO_BUTTON_TONE[type],
              })}

            {action && showCloseButton && (
              <Box
                width={1}
                alignSelf="stretch"
                backgroundColor={theme.colors.border.secondary}
              />
            )}

            {showCloseButton && (
              <IconButton
                variant="tertiary"
                tone="mono"
                size="xs"
                onPress={onClose}
                aria-label="Close toast"
                icon={<Remove size={18} />}
                hoverBackground="none"
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

Toast.displayName = "Toast";
